import { toast as toastify, ToastOptions, Id } from "react-toastify";

type CustomToastOptions = Omit<ToastOptions, 'toastId'> & {
  description?: React.ReactNode;
  // Sonner compatibility aliases
  duration?: number;
  id?: Id;
  dismissible?: boolean;
};

function normalizeOptions(options?: CustomToastOptions): ToastOptions | undefined {
  if (!options) return undefined;

  const { duration, id, description, dismissible, ...rest } = options;
  return {
    ...rest,
    // Map sonner's 'duration' to react-toastify's 'autoClose'
    autoClose: options.autoClose ?? duration,
    // Map sonner's 'id' to react-toastify's 'toastId'
    toastId: (rest as any).toastId ?? id,
    // Map sonner's 'dismissible' to react-toastify's 'closeButton'
    closeButton: dismissible !== undefined ? dismissible : rest.closeButton,
  };
}

/**
 * Consistent toast helpers for the app.
 * Use these instead of calling react-toastify's toast directly to ensure consistent UI.
 */
export const toast = Object.assign(
  // Default toast function
  (message: string | React.ReactNode, options?: CustomToastOptions) => {
    return toastify(message, normalizeOptions(options));
  },
  {
    success: (message: string | React.ReactNode, options?: CustomToastOptions) => {
      const normalized = normalizeOptions(options) || {};
      return toastify.success(message, {
        autoClose: normalized.autoClose ?? 3000,
        ...normalized,
      });
    },

    error: (message: string | React.ReactNode, options?: CustomToastOptions) => {
      const normalized = normalizeOptions(options) || {};
      return toastify.error(message, {
        autoClose: normalized.autoClose ?? 5000,
        ...normalized,
      });
    },

    info: (message: string | React.ReactNode, options?: CustomToastOptions) => {
      const normalized = normalizeOptions(options) || {};
      return toastify.info(message, {
        autoClose: normalized.autoClose ?? 3000,
        ...normalized,
      });
    },

    warning: (message: string | React.ReactNode, options?: CustomToastOptions) => {
      const normalized = normalizeOptions(options) || {};
      return toastify.warning(message, {
        autoClose: normalized.autoClose ?? 4000,
        ...normalized,
      });
    },

    loading: (message: string | React.ReactNode, options?: CustomToastOptions) => {
      return toastify.loading(message, normalizeOptions(options));
    },

    promise: toastify.promise,

    dismiss: (toastId?: Id) => {
      return toastify.dismiss(toastId);
    },

    update: toastify.update,
  }
);

// Legacy export for backwards compatibility
export function customToast(
  message: string,
  description?: string,
  duration?: number,
  type?: "success" | "error" | "info" | "warning"
) {
  const options: CustomToastOptions = { autoClose: duration };

  switch (type) {
    case "error":
      toast.error(message, options);
      break;
    case "warning":
      toast.warning(message, options);
      break;
    case "info":
      toast.info(message, options);
      break;
    case "success":
    default:
      toast.success(message, options);
      break;
  }
}
